import { useEffect } from "react"
import { X } from "lucide-react"
import { useIsMobile } from "@/app/hooks/useIsMobile"
import { Field, Input, ErrorBanner } from "@/app/components/FormPrimitives"
import { secondaryButton, primaryButton } from "@/app/lib/buttonStyles"
import type { ClientTheme } from "@/app/lib/themes"

export const blankLeaseForm = {
  lessor: "",
  lessee: "",
  lease_date: "",
  term_years: "",
  extension_years: "",
  extension_exercised_on: "",
  book: "",
  page: "",
  county: "",
  str: "",
  legal: "",
}

export type LeaseFormState = typeof blankLeaseForm

// Add/edit lease dialog — mirrors UnitsPage's WellFormModal shell (overlay +
// blur, ESC close, body scroll lock, full-screen on mobile). Owns no state;
// the page passes form state and handlers down.
export function LeaseFormModal({
  editing,
  form,
  set,
  onSubmit,
  onClose,
  saving,
  error,
  theme,
}: {
  editing: boolean
  form: LeaseFormState
  set: <K extends keyof LeaseFormState>(key: K, value: LeaseFormState[K]) => void
  onSubmit: (e: React.FormEvent) => void
  onClose: () => void
  saving: boolean
  error: string | null
  theme: ClientTheme
}) {
  const isMobile = useIsMobile()

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [onClose])

  useEffect(() => {
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = ""
    }
  }, [])

  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center ${isMobile ? "items-stretch" : "items-center p-4"}`}
      style={{ backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)" }}
      onClick={onClose}
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className={`relative flex flex-col overflow-y-auto ${isMobile ? "" : "rounded-2xl"}`}
        style={{
          backgroundColor: theme.bgSurface,
          boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
          width: isMobile ? "100vw" : "min(680px, 95vw)",
          height: isMobile ? "100dvh" : undefined,
          maxHeight: isMobile ? undefined : "90vh",
        }}
      >
        <div
          className="flex items-center justify-between px-6 pt-5 pb-4"
          style={{ borderBottom: `1px solid ${theme.borderColor}` }}
        >
          <span
            style={{
              fontFamily: "var(--font-heading)",
              fontSize: "15px",
              fontWeight: 600,
              color: theme.textPrimary,
            }}
          >
            {editing ? "Edit Lease" : "Add Lease"}
          </span>
          <button
            type="button"
            onClick={onClose}
            title="Close"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: theme.textMuted,
              padding: "4px",
            }}
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
            <Field label="Lessor" theme={theme} required>
              <Input value={form.lessor} onChange={(v) => set("lessor", v)} theme={theme} />
            </Field>
            <Field label="Lessee" theme={theme}>
              <Input value={form.lessee} onChange={(v) => set("lessee", v)} theme={theme} />
            </Field>
            <Field label="Lease Date" theme={theme}>
              <Input
                type="date"
                value={form.lease_date}
                onChange={(v) => set("lease_date", v)}
                theme={theme}
              />
            </Field>
            <Field label="County" theme={theme} required>
              <Input value={form.county} onChange={(v) => set("county", v)} theme={theme} />
            </Field>
            <Field label="Term (years)" theme={theme}>
              <Input
                type="number"
                step="any"
                value={form.term_years}
                onChange={(v) => set("term_years", v)}
                theme={theme}
              />
            </Field>
            <Field label="Extension (years)" theme={theme}>
              <Input
                type="number"
                step="any"
                value={form.extension_years}
                onChange={(v) => set("extension_years", v)}
                theme={theme}
              />
            </Field>
            <Field label="Extension Exercised On" theme={theme}>
              <Input
                type="date"
                value={form.extension_exercised_on}
                onChange={(v) => set("extension_exercised_on", v)}
                theme={theme}
              />
            </Field>
            <Field label="STR" theme={theme}>
              <Input
                placeholder="14-13N-19W"
                value={form.str}
                onChange={(v) => set("str", v)}
                theme={theme}
              />
            </Field>
            <Field label="Book" theme={theme}>
              <Input value={form.book} onChange={(v) => set("book", v)} theme={theme} />
            </Field>
            <Field label="Page" theme={theme}>
              <Input value={form.page} onChange={(v) => set("page", v)} theme={theme} />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Legal Description" theme={theme}>
                <textarea
                  value={form.legal}
                  onChange={(e) => set("legal", e.target.value)}
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "8px 11px",
                    borderRadius: "8px",
                    backgroundColor: theme.bgSurface2,
                    border: `1px solid ${theme.borderColor}`,
                    color: theme.textPrimary,
                    fontFamily: "var(--font-ui)",
                    fontSize: "13px",
                    outline: "none",
                    resize: "vertical",
                  }}
                />
              </Field>
            </div>
          </div>

          {error && <ErrorBanner theme={theme} message={error} />}
        </div>

        <div className="px-6 pb-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="btn w-full sm:w-auto"
            style={{ ...secondaryButton(theme), justifyContent: "center" }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn w-full sm:w-auto"
            style={{ ...primaryButton(theme), justifyContent: "center", opacity: saving ? 0.6 : 1 }}
          >
            {saving ? "Saving…" : editing ? "Save Changes" : "Add Lease"}
          </button>
        </div>
      </form>
    </div>
  )
}
